import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { Color, Euler, Object3D, Shader, Vector2, Vector3 } from 'three';

export default function ContactShadows() {
  const { scene } = useThree();
  return (
    <>
      <ContactShadowsImpl
        character={scene.getObjectByName('pelvis')}
        position={[0, 0, 0]}
        opacity={0.33}
        scale={10}
        width={5}
        height={5}
      />
    </>
  );
}

function ContactShadowsImpl({
  character = null,
  position,
  rotation,
  opacity = 1,
  scale = 1,
  width = 1,
  height = 1,
  color = [0, 0, 0],
}: {
  character?: Object3D | null;
  position?: [number, number, number];
  rotation?: [number, number, number];
  opacity?: number;
  scale?: number;
  width?: number;
  height?: number;
  color?: ConstructorParameters<typeof Color>;
}) {
  const refs = useRef<{ shader: null | Shader }>({ shader: null });

  useEffect(() => {
    if (character) {
      character.matrixWorldAutoUpdate = true;
    }
  }, [character]);
  useEffect(() => {
    if (refs.current.shader) {
      refs.current.shader.uniforms.uDimensions.value.set(width, height);
    }
  }, [width, height]);
  useFrame(() => {
    if (!refs.current.shader) {
      return;
    }
    const vector = refs.current.shader.uniforms.uRootPosition.value;
    if (character) {
      vector.setFromMatrixPosition(character.matrixWorld);
    } else {
      vector.set(0, 0, 0);
    }
  });

  function onBeforeCompile(shader: Shader) {
    refs.current.shader = shader;

    shader.uniforms.uAlphaFadeSize = { value: 0.04 };
    shader.uniforms.uRootPosition = { value: new Vector3() };
    shader.uniforms.uDimensions = { value: new Vector2(width, height) };

    // Parameters
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      `
      varying vec2 vUv;

      #include <common>
    `,
    );
    // Main
    shader.vertexShader = shader.vertexShader.replace(
      '#include <fog_vertex>',
      `
      #include <fog_vertex>
      vUv = uv;
    `,
    );

    // Parameters
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `
      uniform float uAlphaFadeSize;
      uniform vec3 uRootPosition;
      uniform vec2 uDimensions;

      varying vec2 vUv;
      #include <common>
    `,
    );
    // Main
    // TODO: Why is vRoot times 0.25?
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <fog_fragment>',
      `
      #include <fog_fragment>
      vec2 vRoot = 0.25 * uRootPosition.xz / uDimensions;
      float t = length( 2.0 * ( vUv - 0.5 ) - vec2( vRoot.x, - vRoot.y ) );
      t = smoothstep( 0.0, uAlphaFadeSize, t );
      gl_FragColor.a = mix( gl_FragColor.a, 0.0, t );
    `,
    );
  }
  return (
    <mesh
      position={position}
      rotation={rotation || new Euler(-Math.PI / 2, 0, 0)}
      scale={[scale, scale, scale]}
      receiveShadow={true}
    >
      <planeGeometry attach="geometry" args={[width, height]} />
      <shadowMaterial
        color={color}
        opacity={opacity}
        onBeforeCompile={onBeforeCompile}
      />
    </mesh>
  );
}
